import React, { useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, Upload } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { ModalSuccess } from './ModalSuccess';
import { normalizeCourse, productLabel } from '../../utils/trainingCourses';
import { CourseStatus, TrainingCourseDraft } from '../../types/trainingCourse';
interface CourseImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  status: CourseStatus;
  onImport: (courses: TrainingCourseDraft[]) => void;
}
type Step = 'upload' | 'preview' | 'success';
function sampleRows(status: CourseStatus): TrainingCourseDraft[] {
  return [{
    name: 'Formazione generale lavoratori — Sessione dicembre',
    productCode: 'FOR-FGL',
    courseCode: 'FGL-2026-12',
    externalProduct: false,
    characteristic: 'Unico',
    commission: 'SEFORM',
    venue: 'seform',
    status
  }, {
    name: 'Preposto — Sessione dicembre',
    productCode: 'FOR-PRE',
    courseCode: 'PRE-2026-12',
    externalProduct: false,
    characteristic: 'Unico',
    commission: 'SEFORM',
    venue: 'blended',
    status
  }, {
    name: 'Primo soccorso gruppo B/C — Sessione dicembre',
    productCode: 'FOR-PSB',
    courseCode: 'PSB-2026-12',
    externalProduct: true,
    characteristic: 'Unico',
    commission: 'SEFORM',
    venue: 'fad',
    status
  }];
}
export function CourseImportModal({
  isOpen,
  onClose,
  status,
  onImport
}: CourseImportModalProps) {
  const [step, setStep] = useState<Step>('upload');
  const [rows, setRows] = useState<TrainingCourseDraft[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleClose = () => {
    setStep('upload');
    setRows([]);
    onClose();
  };
  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return;
    setRows(sampleRows(status).map(normalizeCourse));
    setStep('preview');
    e.target.value = '';
  };
  const handleConfirm = () => {
    onImport(rows);
    setStep('success');
    setTimeout(handleClose, 1600);
  };
  return <Modal isOpen={isOpen} onClose={handleClose} title="Import massivo corsi" size="lg" data-id="element-1233" data-mp="2b7tzzynjlakw">
      <AnimatePresence mode="wait" data-id="element-1234" data-mp="21fwn8ngvyak2">
        {step === 'upload' && <motion.div key="upload" exit={{
        opacity: 0
      }} data-id="element-1235" data-mp="21dk9kmi5umct">
            <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center" data-id="element-1236" data-mp="1whmqi8fij1wc">
              <Upload className="h-12 w-12 text-slate-300 mx-auto mb-4" data-id="element-1237" data-mp="1rgfiq9dde2po" />
              <p className="text-slate-600 mb-2" data-id="element-1238" data-mp="1nfltnco6yhv7">
                Trascina qui il file CSV o clicca per selezionarlo
              </p>
              <p className="text-sm text-slate-400 mb-4" data-id="element-1239" data-mp="1ii3v8g7svl1g">
                Formato supportato: CSV con colonne Nome corso, Codice
                prodotto, Codice corso
              </p>
              <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleFile} data-id="element-1240" data-mp="3cvv2j92npstq" />
              <Button onClick={() => fileInputRef.current?.click()} startContent={<Upload className="h-4 w-4" data-id="element-1242" data-mp="3u57450agn1g2" />} data-id="element-1241" data-mp="3pcdzecezrt3z">
                Seleziona file
              </Button>
            </div>
          </motion.div>}

        {step === 'preview' && <motion.div key="preview" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} className="space-y-6" data-id="element-1243" data-mp="31gi99izzzscr">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2" data-id="element-1244" data-mp="3bg0stybzxc4a">
              <CheckCircle2 className="h-5 w-5 text-green-600" data-id="element-1245" data-mp="qnfgfy3cx3no" />
              <span className="text-green-700 font-medium" data-id="element-1246" data-mp="qmvv07ehzx4w">
                {rows.length} corsi trovati nel file
              </span>
            </div>
            <div className="border border-slate-100 rounded-xl overflow-hidden max-h-64 overflow-y-auto" data-id="element-1247" data-mp="t7lmqcfg167e">
              <table className="w-full text-sm text-left" data-id="element-1248" data-mp="o9a7fm6dq3hs">
                <thead className="bg-slate-50 text-slate-500 font-semibold sticky top-0" data-id="element-1249" data-mp="oav7frbgqen1">
                  <tr data-id="element-1250" data-mp="137cck0t26x15">
                    <th className="p-3" data-id="element-1251" data-mp="y6y6upexjz6a">Nome corso</th>
                    <th className="p-3" data-id="element-1252" data-mp="y5dacu92265s">Tipologia · codice prodotto</th>
                    <th className="p-3" data-id="element-1253" data-mp="1c5xv3vyj6c1x">Codice corso</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-id="element-1254" data-mp="178eg2tu0dv8x">
                  {rows.map(row => <tr key={row.courseCode} data-id="element-1255" data-mp="1lvhd6zef61vq">
                      <td className="p-3 font-medium" data-id="element-1256" data-mp="1lylsartfai16">{row.name}</td>
                      <td className="p-3" data-id="element-1257" data-mp="1gyr1eo9ndsg4">{productLabel(row.productCode)}</td>
                      <td className="p-3 font-mono text-xs" data-id="element-1258" data-mp="1uwfgtgky47ds">
                        {row.courseCode}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-3" data-id="element-1259" data-mp="29kpb995fc5ik">
              <Button variant="flat" color="default" onClick={() => setStep('upload')} data-id="element-1260" data-mp="2ejiod0gstui7">
                Indietro
              </Button>
              <Button onClick={handleConfirm} startContent={<CheckCircle2 className="h-4 w-4" data-id="element-1262" data-mp="1qtep15an8gx0" />} data-id="element-1261" data-mp="25hsxpwuqqzlh">
                Conferma import
              </Button>
            </div>
          </motion.div>}

        {step === 'success' && <ModalSuccess title="Import completato con successo" subtitle={`${rows.length} corsi importati`} data-id="element-1263" data-mp="1ht02k2ly0vrx" />}
      </AnimatePresence>
    </Modal>;
}